import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserStore from '@/store/userStore';
import { jwtDecode } from 'jwt-decode';
import { getToken, getRefreshToken, setToken, removeToken } from '@/utils/tokenStorage';
import { authService } from '@/services/authService';

const AuthChecker = ({ children }) => {
  const { isAuthenticated, logout, login, checkTokenExpiration } = useUserStore();
  const navigate = useNavigate();
  const refreshTimeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const refreshToken = async () => {
    if (isRefreshingRef.current) return false;
    
    isRefreshingRef.current = true;
    
    try {
      const refreshTokenValue = getRefreshToken();
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshTokenValue);
      
      if (response.success && response.data?.access_token) {
        setToken(response.data.access_token);
        
        // Update user store with new token
        const currentUser = useUserStore.getState().user;
        login(currentUser, response.data.access_token);
        
        scheduleTokenRefresh(response.data.access_token);
        return true;
      }
      
      throw new Error('Invalid refresh response');
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      navigate('/auth/login');
      return false;
    } finally {
      isRefreshingRef.current = false;
    }
  };

  const scheduleTokenRefresh = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;
      
      // Refresh token 5 minutes before expiry
      const refreshTime = Math.max(0, (timeUntilExpiry - 300) * 1000);
      
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      refreshTimeoutRef.current = setTimeout(() => {
        refreshToken();
      }, refreshTime);
      
    } catch (error) {
      console.error('Error scheduling token refresh:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      const token = getToken();
      if (token) {
        // Check if token is still valid
        if (checkTokenExpiration()) {
          scheduleTokenRefresh(token);
        }
      }
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [isAuthenticated, checkTokenExpiration]);

  // Handle visibility change to check auth when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        if (!checkTokenExpiration()) {
          // Token expired, try to refresh
          refreshToken();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, checkTokenExpiration]);

  return children;
};

export default AuthChecker;